'use client';

import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface SuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  guestName: string;
}

const SuccessModal = ({ isOpen, onClose, guestName }: SuccessModalProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (isOpen && isHydrated) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isHydrated]);

  if (!isOpen || !isHydrated) return null;

  const handleDownloadCalendar = () => {
    const event = {
      title: 'Moyak & Gloria Wedding Celebration',
      description: 'Join us as we celebrate our sacred union in the presence of God, family, and friends.',
      location: 'Wedding Venue Address',
      startDate: '2026-06-20T14:00:00',
      endDate: '2026-06-20T22:00:00',
    };

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Moyak & Gloria Wedding//EN
BEGIN:VEVENT
UID:${Date.now()}@moyakgloriawedding.com
DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z
DTSTART:${event.startDate.replace(/[-:]/g, '').split('.')[0]}Z
DTEND:${event.endDate.replace(/[-:]/g, '').split('.')[0]}Z
SUMMARY:${event.title}
DESCRIPTION:${event.description}
LOCATION:${event.location}
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = 'moyak-gloria-wedding.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-sacred-lg max-w-md w-full p-6 sm:p-8 relative animate-fadeIn">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-surface transition-colors duration-200"
          aria-label="Close modal"
        >
          <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
        </button>

        <div className="text-center">
          <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircleIcon" size={32} className="text-success" variant="solid" />
          </div>

          <h2 className="font-playfair text-2xl font-semibold text-foreground mb-2">
            Thank You, {guestName}!
          </h2>

          <p className="font-inter text-sm text-muted-foreground mb-6">
            Your RSVP has been received. We are blessed to have you join us on our special day. A confirmation email with all the details has been sent to your email address.
          </p>

          <div className="bg-surface rounded-lg p-4 mb-6 border border-border">
            <div className="flex items-start space-x-3">
              <Icon name="CalendarIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
              <div className="text-left">
                <p className="font-playfair text-lg font-semibold text-foreground">
                  Save the Date
                </p>
                <p className="font-inter text-xs text-muted-foreground">
                  Wednesday, November 20, 2026 at 2:00 PM
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={handleDownloadCalendar}
            className="w-full flex items-center justify-center space-x-2 px-6 py-3 rounded-md bg-primary text-primary-foreground font-inter text-sm font-medium hover:bg-primary/90 transition-all duration-200 shadow-md mb-3"
          >
            <Icon name="ArrowDownTrayIcon" size={18} />
            <span>Add to Calendar</span>
          </button>

          <button
            onClick={onClose}
            className="w-full px-6 py-3 rounded-md border border-border bg-surface text-foreground font-inter text-sm font-medium hover:bg-input transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuccessModal;